import {
	BILLING_PROFILE_ASSOCIATION_SETUP_SCREEN_SLUG,
	INITIAL_SNACKBAR_DATA,
	QUERY_OPTIONS,
} from "../../../common/constants";
import {
	BackdropCircularProgress,
	DNDTwoColumnList,
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	IDNDList,
	IEnhancedDropDownItem,
	IServerError,
	ITest,
	LabelPosition,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { Box, Typography, useTheme } from "@mui/material";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";

import { BillingProfileClientsAssignmentSchema } from "./validator";
import Grid from "@mui/material/Grid";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IResponse } from "../../../models";
import { TaxProfileService } from "../../../services/TaxProfileService";
import { instanceOfIBillingProfileScreenPanelsAssignmentResponsePayload } from "../../../common/instance-method";
import { setSelectedActivityLogActions } from "../../../store/actions/activityLogActions";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../store/actions";
import { sortDDLByLabel } from "../../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

export interface ITaxProfileAssignment extends ITest { }
const firstPanelDraggableId = "firstList";
const secondPanelDraggableId = "secondList";

const BILLING_PROFILE_ASSOCIATION_ASSIGNED_PANELS_QUERY_KEY =
	"billing-profile-association-assigned-panels";

const useGetProfilesDropDownList = () =>
	useQuery({
		queryKey: ["get-billing-profile-dll"],
		queryFn: () => TaxProfileService.getProfilesDropDownList(),
		refetchOnWindowFocus: false,
	});

const useGetAccountantsDropDownList = () =>
	useQuery({
		queryKey: ["get-accountant-dll"],
		queryFn: () => TaxProfileService.getAccountantsDropDownList(),
		refetchOnWindowFocus: false,
	});

export const TaxProfileAssignment: FC<ITaxProfileAssignment> = ({ testId }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const [selectedAccountant, setSelectedAccountant] = useState("All");
	const [accountantListItem, setAccountantListItem] = useState<
		IDropDownListResponse[]
	>([{ label: "All", value: "All" }]);
	const [selectedBillingProfile, setSelectedBillingProfile] = useState("");
	const [billingAssociationId, setBillingAssociationId] = useState<
		string | null | undefined
	>();
	const [firstList, setFirstList] = useState<IDNDList>({
		droppableId: "firstList",
		elements: [],
		testId: "firstList",
	});
	const [secondList, setSecondList] = useState<IDNDList>({
		droppableId: "secondList",
		elements: [],
		testId: "secondList",
	});
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoadingData, setIsLoadingData] = useState(false);

	const useGetAssignedPanels = () =>
		useQuery({
			queryKey: [
				BILLING_PROFILE_ASSOCIATION_ASSIGNED_PANELS_QUERY_KEY,
				selectedAccountant,
				selectedBillingProfile,
			],
			queryFn: () =>
				TaxProfileService.getPanelsAssignedToScreen(
					selectedAccountant,
					selectedBillingProfile
				),
			enabled: false,
			...QUERY_OPTIONS,
		});

	const {
		isLoading: isLoadingTabDropDownList,
		data: profileList,
		// refetch: refetchTabsDDL,
	} = useGetProfilesDropDownList();

	const {
		isLoading: isLoadingAccountantDropDownList,
		data: accountantList,
		// refetch: refetchTabsDDL,
	} = useGetAccountantsDropDownList();

	const {
		isLoading: isLoadingAssignedClients,
		data: assignedPanelResponsePayload,
		refetch: refetchAssignedPanels,
		// } = useGetAssignedPanels(selectedAccountant,selectedBillingProfile);
	} = useGetAssignedPanels();

	const { isPending: isSubmitting, mutate: submitFormData } = useMutation({
		mutationFn: TaxProfileService.createProfileAssociation,
		onSuccess: (res: IServerError | IResponse | null | any) => {
			if (res?.status === 201) {
				setSnackbarData({
					open: true,
					message:
						"Client has been assigned to the Billing Profile successfully",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message:
						"Client has been unassigned from the Billing Profile successfully",
					severity: "warning",
				});
			}
		},
		onError: (err) => {
			setSnackbarData({
				open: true,
				message: "An error occurred, please try again",
				severity: "error",
			});
		},
	});

	const DEFAULT_VALUES = {
		selectedAccountant: "All",
		selectedTaxProfile: undefined

	};

	const formMethods = useForm<any>({
		resolver: yupResolver(BillingProfileClientsAssignmentSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (formData: any) => { };

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
	};

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const onDragEnd = (result: any, _provided: any) => {
		if (
			result.destination &&
			result.source.droppableId !== result.destination.droppableId
		) {
			if (result.source.droppableId === "firstList") {
				let sourceList = firstList;
				let destinationList = secondList;
				let movedItem = sourceList.elements.splice(result.source.index, 1)[0];
				destinationList.elements.splice(result.destination.index, 0, movedItem);
				setFirstList({
					...sourceList,
				});
				setSecondList({
					...destinationList,
				});
			} else {
				let sourceList = secondList;
				let destinationList = firstList;
				let movedItem = sourceList.elements.splice(result.source.index, 1)[0];
				destinationList.elements.splice(result.destination.index, 0, movedItem);
				setSecondList({
					...sourceList,
				});
				setFirstList({
					...destinationList,
				});
			}
		} else {
			if (!result.destination) return;
			if (result.source.droppableId === "firstList") {
				let sourceDestinationList = firstList;
				let sourceItem = sourceDestinationList.elements.splice(
					result.source.index,
					1
				)[0];
				sourceDestinationList.elements.splice(
					result.destination.index,
					0,
					sourceItem
				);
				setFirstList({
					...sourceDestinationList,
				});
			} else {
				let sourceDestinationList = secondList;
				let sourceItem = sourceDestinationList.elements.splice(
					result.source.index,
					1
				)[0];
				sourceDestinationList.elements.splice(
					result.destination.index,
					0,
					sourceItem
				);
				setSecondList({
					...sourceDestinationList,
				});
			}
		}
		if (result.destination.droppableId === "secondList") {
			//this call is for accosiate clients
			let Payload = {
				profileId: selectedBillingProfile,
				clientId: result.draggableId,
				flag: true,
				// billingAssociationId: billingAssociationId,
			};
			submitFormData(Payload);
		} else {
			//this call is for UnAccosiate clients
			let Payload = {
				profileId: selectedBillingProfile,
				clientId: result.draggableId,
				flag: false,
				billingAssociationId: billingAssociationId,
			};
			submitFormData(Payload);
		}
	};

	useEffect(() => {
		if (
			assignedPanelResponsePayload &&
			instanceOfIBillingProfileScreenPanelsAssignmentResponsePayload(
				assignedPanelResponsePayload
			)
		) {
			let unassignedClients: IDNDList = {
				droppableId: firstPanelDraggableId,
				elements: assignedPanelResponsePayload.unassignedClients.map(
					(unassignedFlexField: any) => ({
						id: String(unassignedFlexField.id),
						text: String(unassignedFlexField.text),
						value: String(unassignedFlexField.value),
					})
				),
				testId: firstPanelDraggableId,
			};

			let assignedClients: IDNDList = {
				droppableId: secondPanelDraggableId,
				elements: assignedPanelResponsePayload.assignedClients.map(
					(assignedFlexField: any) => ({
						id: String(assignedFlexField.id),
						text: String(assignedFlexField.text),
						value: String(assignedFlexField.value),
					})
				),
				testId: secondPanelDraggableId,
			};
			setBillingAssociationId(
				assignedPanelResponsePayload.billingAssociationId
			);
			setFirstList(unassignedClients);
			setSecondList(assignedClients);
		}
	}, [assignedPanelResponsePayload]);

	const profileListItems = useMemo(() => {

		if (profileList && Array.isArray(profileList)) {
			return profileList;
		}

		return [];
	}, [profileList]);

	const accountantListItems = useMemo(() => {
		if (accountantList && Array.isArray(accountantList)) {
			let resources: IDropDownListResponse[] = sortDDLByLabel(accountantList).map(
				(resourceItem: IDropDownListResponse) => ({
					id: resourceItem.id,
					label: resourceItem.label,
					value: resourceItem.value,
				})
			);

			resources.unshift({
				label: "All",
				value: "All",
			});
			setAccountantListItem(accountantList);
			return resources;
		}

		return [
			{
				label: "All",
				value: "All",
			},
		];
	}, [accountantList]);


	useEffect(() => {
		if (selectedBillingProfile && selectedBillingProfile.length > 0) {
			refetchAssignedPanels();
		}
	}, [refetchAssignedPanels, selectedBillingProfile]);

	const onAccountantSelectionChangeHandler = (
		item: IEnhancedDropDownItem
	): void => {
		let selectedValue = String(item.value);
		if (selectedAccountant !== selectedValue) {
			setSelectedAccountant(selectedValue);
			setSelectedBillingProfile("");
			formMethods.setValue("selectedTaxProfile", "");
		}
	};

	const onBillingProfileSelectionChangeHandler = (
		item: IEnhancedDropDownItem
	) => {
		setSelectedBillingProfile(String(item.value));
	};

	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);

	const expandedLabels = ["Misc."];
	const expandedBillingProfileAssociationSetupItem = "Profile Association";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Misc." &&
				item.label === expandedBillingProfileAssociationSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedBillingProfileAssociationSetupItem && "/billing-profile-association",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: BILLING_PROFILE_ASSOCIATION_SETUP_SCREEN_SLUG,
				companyName: "",
				activityType: "activity_type=create",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<>
			<BackdropCircularProgress
				open={
					isSubmitting ||
					isLoadingTabDropDownList ||
					isLoadingAccountantDropDownList ||
					isLoadingAssignedClients
				}
				testId={`${testId}-backdrop`}
			/>
			<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<Grid
					size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
					sx={{ "& .MuiGrid-container": { marginTop: 0 } }}
				>
					<FormProvider {...formMethods}>
						<form onSubmit={formMethods.handleSubmit(onSubmit)}>
							<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<Grid
									size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
									sx={{
										borderRadius: "3px",
									}}
								>
									<SingleColumnLayout
										showFullDiaplay={true}
										showToolbar={false}
										children={
											<>
												<StandardPanel
													title="Billing Profile Association"
													testId={`${testId}-screen-heading-panel`}
													leftSpacing={2}
													rightSpacing={2}
													topPadding={2}
												>
													<EnhancedDropDownRHFWithLabel
														testId={`${testId}-name`}
														label="Select Accountant"
														labelPosition={LabelPosition.Left}
														// required
														selectName="selectedAccountant"
														items={accountantListItems}
														selectedItem={"All"}
														onSelectionChangeHandler={
															onAccountantSelectionChangeHandler
														}
													/>
													<EnhancedDropDownRHFWithLabel
														testId={`${testId}-name`}
														label="Select Profile"
														labelPosition={LabelPosition.Left}
														// required
														selectName="selectedTaxProfile"
														items={profileListItems}
														selectedItem={selectedBillingProfile}
														onSelectionChangeHandler={
															onBillingProfileSelectionChangeHandler
														}
													/>
												</StandardPanel>
												{selectedAccountant.length > 0 &&
													selectedBillingProfile.length > 0 && (
														<Box sx={{ pt: theme.spacing(3) }}>
															<DragDropContext onDragEnd={onDragEnd}>
																<Grid container spacing={2}>
																	{/* UnAssociations */}
																	<Grid size={{ xl: 6 }}>
																		<Typography
																			variant="h6"
																			sx={{
																				display: "flex",
																				alignItems: "center",
																			}}
																		>
																			UnAssociations
																			<Box
																				component="span"
																				sx={{
																					ml: 1,
																					px: 1.5,
																					py: 0.2,
																					bgcolor: "primary.main",
																					color: "#fff",
																					borderRadius: "50%",
																					fontSize: "0.8rem",
																				}}
																			>
																				{firstList.elements.length}
																			</Box>
																		</Typography>

																		<Droppable
																			droppableId={firstList.droppableId}
																		>
																			{(provided) => (
																				<Box
																					ref={provided.innerRef}
																					{...provided.droppableProps}
																					sx={{
																						minHeight: "200px",
																						mt: 2,
																						border: "1px solid #ddd",
																						borderRadius: 2,
																						p: 1,
																						bgcolor: "#f9f9f9",
																						maxHeight: "400px",
																						overflowY: "auto",
																					}}
																				>
																					{firstList.elements.map(
																						(item, index) => (
																							<Draggable
																								key={item.id}
																								draggableId={item.id}
																								index={index}
																							>
																								{(provided) => (
																									<Box
																										ref={provided.innerRef}
																										{...provided.draggableProps}
																										{...provided.dragHandleProps}
																										sx={{
																											bgcolor: "#fff",
																											mb: 1,
																											p: 1.5,
																											borderRadius: 1,
																											border: "1px solid #ccc",
																											cursor: "grab",
																										}}
																									>
																										{item.text}
																									</Box>
																								)}
																							</Draggable>
																						)
																					)}
																					{provided.placeholder}
																				</Box>
																			)}
																		</Droppable>
																	</Grid>

																	{/* Associations */}
																	<Grid size={{ xl: 6 }}>
																		<Typography
																			variant="h6"
																			sx={{
																				display: "flex",
																				alignItems: "center",
																			}}
																		>
																			Associations
																			<Box
																				component="span"
																				sx={{
																					ml: 1,
																					px: 1.5,
																					py: 0.2,
																					bgcolor: "primary.main",
																					color: "#fff",
																					borderRadius: "50%",
																					fontSize: "0.8rem",
																				}}
																			>
																				{secondList.elements.length}
																			</Box>
																		</Typography>

																		<Droppable
																			droppableId={secondList.droppableId}
																		>
																			{(provided) => (
																				<Box
																					ref={provided.innerRef}
																					{...provided.droppableProps}
																					sx={{
																						minHeight: "200px",
																						mt: 2,
																						border: "1px solid #ddd",
																						borderRadius: 2,
																						p: 1,
																						bgcolor: "#f9f9f9",
																						maxHeight: "400px",
																						overflowY: "auto",
																					}}
																				>
																					{secondList.elements.map(
																						(item, index) => (
																							<Draggable
																								key={item.id}
																								draggableId={item.id}
																								index={index}
																							>
																								{(provided) => (
																									<Box
																										ref={provided.innerRef}
																										{...provided.draggableProps}
																										{...provided.dragHandleProps}
																										sx={{
																											bgcolor: "#fff",
																											mb: 1,
																											p: 1.5,
																											borderRadius: 1,
																											border: "1px solid #ccc",
																											cursor: "grab",
																										}}
																									>
																										{item.text}
																									</Box>
																								)}
																							</Draggable>
																						)
																					)}
																					{provided.placeholder}
																				</Box>
																			)}
																		</Droppable>
																	</Grid>
																</Grid>
															</DragDropContext>
														</Box>
													)}

												{/* {selectedAccountant.length > 0 &&
                  selectedBillingProfile.length > 0 && (
                    <Box sx={{ pt: theme.spacing(3) }}>
                      <DragDropContext onDragEnd={onDragEnd}>
                        <h1>Helo this is drag </h1>
                        <DNDTwoColumnList
                          firstColumnListTitle="UnAssociations"
                          firstColumnList={firstList}
                          secondColumnListTitle="Associations"
                          secondColumnList={secondList}
                          testId="billing-profile-assignment"
                        />
                      </DragDropContext>
                    </Box>
                  )} */}
											</>
										}
										toolbar={<></>}
										testId="form-layout"
									/>
								</Grid>
								<Grid size={{ xs: 0, sm: 0, md: 1.3, lg: 1.3, xl: 1.3 }}></Grid>
								<Grid
									size={{ xs: 12, sm: 12, md: 2.7, lg: 2.7, xl: 2.7 }}
									sx={{ marginTop: "16px" }}
								></Grid>
							</Grid>
						</form>
					</FormProvider>
				</Grid>
			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`${testId}-snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
